var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export class GoWrappedError extends Error {
    constructor(reason) {
        super('' + reason);
        this.reason = reason;
    }
}
// NOTE: This needs to be written using 'function' syntax (cannot be arrow function)
// See: https://github.com/microsoft/TypeScript/issues/34523#issuecomment-542978853
export function assertGoSuccess(result) {
    if (!result.success) {
        throw result.error;
    }
}
// NOTE: This needs to be written using 'function' syntax (cannot be arrow function)
// See: https://github.com/microsoft/TypeScript/issues/34523#issuecomment-542978853
export function assertGoError(result) {
    if (result.success) {
        throw new Error('Assertion failed. Expected error, but no error was thrown');
    }
}
export const success = (value) => {
    return { success: true, data: value, error: undefined };
};
// We allow the consumer to type which error is returned. The "err" parameter has weaker type ("Error") to accommodate
// for a generic error thrown by the go functions.
export const fail = (err) => {
    return { success: false, data: undefined, error: err };
};
const createGoError = (err) => {
    if (err instanceof Error)
        return fail(err);
    return fail(new GoWrappedError(err));
};
export const goSync = (fn) => {
    try {
        return success(fn());
    }
    catch (err) {
        return createGoError(err);
    }
};
const getRandomInRange = (min, max) => {
    return Math.random() * (max - min) + min;
};
const cancellableSleep = (ms) => {
    let resolveFn;
    let timeoutId;
    const promise = new Promise((resolve) => {
        resolveFn = resolve;
        timeoutId = setTimeout(resolve, ms);
    });
    const cancel = () => {
        clearTimeout(timeoutId);
        resolveFn();
    };
    return {
        promise,
        cancel,
    };
};
const cancellableTimeout = (ms) => {
    let rejectFn;
    let timeoutId;
    const promise = new Promise((_, reject) => {
        rejectFn = reject;
        timeoutId = setTimeout(() => reject('Operation timed out'), ms);
    });
    const cancel = () => {
        clearTimeout(timeoutId);
        rejectFn();
    };
    return {
        promise,
        cancel,
    };
};
const attempt = (fn, attemptTimeoutMs) => __awaiter(void 0, void 0, void 0, function* () {
    let timeout = null;
    // We need try/catch because `fn` might throw sync errors as well
    try {
        if (attemptTimeoutMs === undefined)
            return success(yield fn());
        else {
            timeout = cancellableTimeout(attemptTimeoutMs);
            const result = yield Promise.race([fn(), timeout.promise]);
            timeout.cancel();
            return success(result);
        }
    }
    catch (err) {
        if (timeout === null || timeout === void 0 ? void 0 : timeout.cancel) {
            timeout.cancel();
        }
        return createGoError(err);
    }
});
export const go = (fn, options) => __awaiter(void 0, void 0, void 0, function* () {
    if (!options)
        return attempt(fn);
    const { retries, attemptTimeoutMs, delay, totalTimeoutMs, onAttemptError } = options;
    let fullTimeoutExceeded = false;
    let totalTimeoutCancellable = null;
    let fullTimeoutPromise = new Promise((_resolve) => { }); // Never resolves
    if (totalTimeoutMs !== undefined) {
        // Start a "full" timeout that will stop all retries after it is exceeded
        totalTimeoutCancellable = cancellableSleep(totalTimeoutMs);
        fullTimeoutPromise = totalTimeoutCancellable.promise.then(() => {
            fullTimeoutExceeded = true;
            return fail(new Error('Full timeout exceeded'));
        });
    }
    // Typing as "any" because TS has troubles understanding that the value can be non-null
    let delayCancellable;
    const makeAttempts = () => __awaiter(void 0, void 0, void 0, function* () {
        const attempts = retries ? retries + 1 : 1;
        let lastFailedAttemptResult = null;
        for (let i = 0; i < attempts; i++) {
            // if array of timeouts is provided, use the timeout at the current index,
            // or the last one if the index is out of bounds
            // if a single timeout is provided, use it for all attempts
            let currentAttemptTimeoutMs;
            if (Array.isArray(attemptTimeoutMs)) {
                currentAttemptTimeoutMs = attemptTimeoutMs[i] || attemptTimeoutMs.at(-1);
            }
            else {
                currentAttemptTimeoutMs = attemptTimeoutMs;
            }
            // Return early in case the global timeout has been exceeded during after attempt wait time.
            //
            // This is guaranteed to be false for the first attempt.
            if (fullTimeoutExceeded)
                break;
            const goRes = yield attempt(fn, currentAttemptTimeoutMs);
            // Return early if the timeout is exceeded not to cause any side effects (such as calling "onAttemptError" function)
            if (fullTimeoutExceeded)
                break;
            if (i !== attempts - 1 && !goRes.success && onAttemptError)
                goSync(() => onAttemptError(goRes));
            if (goRes.success)
                return goRes;
            lastFailedAttemptResult = goRes;
            if (delay && i !== attempts - 1) {
                switch (delay.type) {
                    case 'random': {
                        const { minDelayMs, maxDelayMs } = delay;
                        delayCancellable = cancellableSleep(getRandomInRange(minDelayMs, maxDelayMs));
                        yield delayCancellable.promise;
                        break;
                    }
                    case 'static': {
                        const { delayMs } = delay;
                        delayCancellable = cancellableSleep(delayMs);
                        yield delayCancellable.promise;
                        break;
                    }
                }
            }
        }
        return lastFailedAttemptResult;
    });
    const result = yield Promise.race([makeAttempts(), fullTimeoutPromise]);
    if (totalTimeoutCancellable === null || totalTimeoutCancellable === void 0 ? void 0 : totalTimeoutCancellable.cancel)
        totalTimeoutCancellable.cancel();
    if (delayCancellable === null || delayCancellable === void 0 ? void 0 : delayCancellable.cancel)
        delayCancellable.cancel();
    return result;
});
//# sourceMappingURL=index.js.map